import { memo } from 'react';
import type { CSSProperties, ElementType } from 'react';

import classNames from 'classnames';

import type { PolymorphicPropsWithRef } from '@/shared/types';
import { Box } from '@/shared/ui/Box';
import type { OpdsStyleProps } from '@/shared/ui/Box/styleProps';

import styles from './Text.module.css';

export type TextVariant = 'display' | 'headline' | 'title' | 'body' | 'label';
export type TextSize = 'small' | 'regular' | 'medium' | 'large';
export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type TextWhiteSpace = 'normal' | 'nowrap' | 'pre' | 'pre-line' | 'pre-wrap';
export type TextWordBreak = 'normal' | 'break-all' | 'keep-all' | 'break-word';

type TextOwnProps = OpdsStyleProps & {
  /** @default body */
  variant?: TextVariant;

  /** @default medium */
  size?: TextSize;

  align?: TextAlign;
  whiteSpace?: TextWhiteSpace;
  wordBreak?: TextWordBreak;
  truncate?: boolean;

  /** @default false */
  nowrap?: boolean;
  style?: CSSProperties;
};

export type TextProps<C extends ElementType = 'div'> = PolymorphicPropsWithRef<C, TextOwnProps>;

const Text = <C extends ElementType = 'div'>(props: TextProps<C>) => {
  const {
    className,
    children,
    truncate,
    align,
    nowrap = false,
    as = 'div',
    variant = 'body',
    size = 'medium',
    whiteSpace,
    wordBreak,
    style,
    ...rest
  } = props;

  return (
    <Box
      as={as as ElementType}
      className={classNames(className, styles.text, nowrap && styles.nowrap)}
      data-size={size}
      data-variant={variant}
      style={{
        textAlign: align,
        whiteSpace: truncate ? 'nowrap' : whiteSpace,
        overflow: truncate ? 'hidden' : undefined,
        textOverflow: truncate ? 'ellipsis' : undefined,
        wordBreak,
        ...style,
      }}
      {...rest}>
      {children}
    </Box>
  );
};

Text.displayName = 'Text';

export default memo(Text) as typeof Text;
